import React, { useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { bookingFlowStore } from "../../store/BookingFlowStore";
import {
  BookingStep,
  servicebookingFlowStore,
} from "../../store/ServiceBookingFlowStore";
import { doctorsStore } from "../../store/DoctorsStore";
import { TimePicker, TimePickerDateTime } from "../TimePicker";
import { logError } from "../../components/logger";
import { DateTime } from "luxon";
import { PiStethoscopeBold } from "react-icons/pi";
import { dateDifferenceFromNow } from "../../utils/dates";
import { observer } from "mobx-react-lite";
import { formatDate, formatTime, formatDateTime } from "../../lib/util";
import { runInAction } from "mobx";
import logger from "utils/logger";
// import { useUserData } from "../../hooks/useUserData";
import AppointmentTimeSkeleton from "../AppointmentTimeSkeleton";
import useUserDataNew from "../../hooks/useUserDataNew";
import { authStore } from "../../store/AuthStore";

interface Step7Props {
  appointmentSlotsData: any;
  startDate: DateTime;
  setStartDate: (date: DateTime) => void;
  numberOfColumns: number;
  isLoading: boolean;
}

const INITIAL_STATE: TimePickerDateTime = {
  date: "",
  formattedDate: "",
  time: "",
  formattedTime: "",
  duration: "",
};

const Step7_TimeSelection: React.FC<Step7Props> = ({
  appointmentSlotsData,
  startDate,
  setStartDate,
  numberOfColumns,
  isLoading,
}) => {
  const isServiceBooking = servicebookingFlowStore.isServiceBooking;

  const timeSelectionSlots = appointmentSlotsData?.appointmentSlots;

  const [selectedDateTime, setSelectedDateTime] =
    useState<TimePickerDateTime>(INITIAL_STATE);

  const selectedDoctor = isServiceBooking
    ? servicebookingFlowStore.getSelectedDoctor()
    : bookingFlowStore.appointmentData.selectedDoctor;

  const selectedDoctorId = isServiceBooking
    ? servicebookingFlowStore.appointmentData.selectedDoctor?.doctor_id
    : bookingFlowStore.appointmentData.selectedDoctor?.doctor_id;

  doctorsStore.setCurrentDoctor(selectedDoctor);

  const doctorStoreAvailableTimes =
    doctorsStore.selectedAptTypeFirstAvailableTimes?.[selectedDoctorId];

  //DateTime.fromFormat(firstAvailableTimeData.date, 'dd-MM-yyyy').toFormat('yyyy-MM-dd')
  //logger.log("Date Data:", DateTime.fromFormat(firstAvailableTimeData?.date, 'dd-MM-yyyy').toFormat('yyyy-MM-dd'));

  const numberOfColumnsNow = useBreakpointValue({ base: 2, md: 3, lg: 7 });
  const handlePrevDates = () => {
    setSelectedDateTime(INITIAL_STATE);
    setStartDate(startDate.minus({ days: numberOfColumnsNow }));
    logError.info("Previous dates clicked");
  };

  const { isLoggedIn } = useUserDataNew();

  const handleNextDates = () => {
    setSelectedDateTime(INITIAL_STATE);
    setStartDate(startDate.plus({ days: numberOfColumnsNow }));
    logError.info("Next dates clicked");
  };

  const handleSelectTime = (time) => {
    servicebookingFlowStore.setActiveStep(2); 

    const newSelectedTime = {
      formattedDate: doctorStoreAvailableTimes.date,
      formattedTime: time.formattedTime,
      date: time.date,
      time: time.time,
      duration: time.duration,
    };

    // Update booking flow store with the selected time and advance to next step
    runInAction(() => {
      bookingFlowStore.updateAppointmentData("selectedTime", newSelectedTime);
      bookingFlowStore.nextStep();
      bookingFlowStore.setActiveStep(bookingFlowStore.currentStep);
    });

    // Update service booking flow store with the selected time and go to step 4
    runInAction(() => {
      servicebookingFlowStore.updateAppointmentData(
        "selectedTime",
        newSelectedTime
      );

      const accessToken = authStore.accessToken;
      if (!accessToken) {
        //servicebookingFlowStore.comefromLogin();
        servicebookingFlowStore.goToStep(BookingStep.SERVICE_LOGIN);
        servicebookingFlowStore.setActiveStep(2);
      } else {
        servicebookingFlowStore.goToStep(BookingStep.CONFIRMATION);
        servicebookingFlowStore.setActiveStep(3);
      }
    });
  };

  const getFirstBookedAppointment = (slots) => {
    if (!Array.isArray(slots)) {
      // console.error("Expected 'slots' to be an array but got:", slots);
      return {
        date: null,
        time: null,
        message: "Invalid input: slots must be an array.",
      };
    }

    for (const slot of slots) {
      const { date, appointments } = slot;

      if (appointments && appointments.length > 0) {
        const firstValid = appointments.find((a) => a.time);
        if (firstValid) {
          return {
            date,
            time: firstValid.time,
            message: `First booked appointment is at ${firstValid.time} on ${date}.`,
          };
        }
      }
    }

    return {
      date: null,
      time: null,
      message: "No booked appointments found.",
    };
  };

  const firstAvailableTime = getFirstBookedAppointment(timeSelectionSlots);
  logger.log("First Available Time:", firstAvailableTime);
  const formattedDateTime = formatDateTime(
    doctorStoreAvailableTimes?.date,
    doctorStoreAvailableTimes?.time
  );

  const hasValidAppointments = (slots: any[]): boolean => {
    if (!Array.isArray(slots)) return false;
    
    return slots.some(slot => 
      slot?.appointments && 
      Array.isArray(slot.appointments) && 
      slot.appointments.length > 0
    );
};

  const handleSelectFirstAvailableTime = () => {
    // Wrap the entire function in runInAction to ensure all observable readings happen in a reactive context
    runInAction(() => {
      if (
        !doctorStoreAvailableTimes ||
        !doctorStoreAvailableTimes.date ||
        !doctorStoreAvailableTimes.time
      ) {
        console.warn("No first available time found.");
        return;
      }

      // Find the appointment slot that matches the first available date
      const matchingSlot = timeSelectionSlots?.find(
        (slot) => slot.date === doctorStoreAvailableTimes.date
      );

      // Find the specific appointment in the slot that matches the first available time
      const matchingAppointment = matchingSlot?.appointments?.find(
        (appt) => appt.time === doctorStoreAvailableTimes.time
      );

      const newSelectedTime = {
        formattedDate: formatDate(doctorStoreAvailableTimes.date),
        formattedTime: formatTime(
          doctorStoreAvailableTimes.time.split(":")[0],
          doctorStoreAvailableTimes.time.split(":")[1]
        ),
        date: doctorStoreAvailableTimes.date,
        time: doctorStoreAvailableTimes.time,
        duration:
          (isServiceBooking
            ? servicebookingFlowStore?.appointmentData?.selectedDoctor
                ?.first_slot?.duration
            : matchingAppointment?.duration) || "N/A",
      };

      logger.log("selectedTime", newSelectedTime);
      logger.log("selectedTime1", matchingAppointment?.duration);

      // Update the booking flow store and advance to next step
      bookingFlowStore.updateAppointmentData("selectedTime", newSelectedTime);
      bookingFlowStore.nextStep();
      bookingFlowStore.setActiveStep(bookingFlowStore.currentStep);

      // Update the service booking flow store and advance to next step
      servicebookingFlowStore.updateAppointmentData(
        "selectedTime",
        newSelectedTime
      );
      logger.log(
        "servicebookingFlowStore.currentStep",
        servicebookingFlowStore.currentStep
      );

      if (!isLoggedIn) {
        //servicebookingFlowStore.comefromLogin();
        servicebookingFlowStore.goToStep(BookingStep.SERVICE_LOGIN);
        servicebookingFlowStore.setActiveStep(2);
      } else {
        servicebookingFlowStore.goToStep(BookingStep.CONFIRMATION);
        servicebookingFlowStore.setActiveStep(3);
      }
    });
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
    >
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Select your Appointment Time
      </Text>

      <Box width={{ base: "95%", lg: "95%", xl: "60%" }}>
        {/* First Available Time Card */}
        {formattedDateTime ? (
          <Box
            borderRadius="full"
            bg={
              isServiceBooking
                ? servicebookingFlowStore?.configurationDetails?.colorScheme
                    ?.buttonBg
                : "#B92F2F"
            }
            height="60px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            mb={4}
            cursor="pointer"
            onClick={handleSelectFirstAvailableTime}
          >
            <IconButton
              isRound
              variant="solid"
              bg="#fff"
              border="2px"
              borderColor={
                isServiceBooking
                  ? servicebookingFlowStore?.configurationDetails?.colorScheme
                      ?.buttonBg
                  : "#B92F2F"
              } //
              color={
                isServiceBooking
                  ? servicebookingFlowStore?.configurationDetails?.colorScheme
                      ?.buttonBg
                  : "#B92F2F"
              } //
              aria-label="Select First Available Time"
              fontSize="20px"
              icon={<PiStethoscopeBold />}
              _hover={{
                bg: isServiceBooking
                  ? servicebookingFlowStore?.configurationDetails?.colorScheme
                      ?.buttonBg
                  : "#B92F2F",
                color: "white",
              }}
            />
            <Box pl={4} pr={4}>
              <Text fontSize="md" fontWeight="bold" color="white">
                Book first available time
              </Text>
              <Text fontSize="xs" color="white">
                {formattedDateTime || ""}
              </Text>
            </Box>
          </Box>
        ) : null}
    
        {hasValidAppointments(timeSelectionSlots) && !formattedDateTime ? (
          <AppointmentTimeSkeleton />
        ) : timeSelectionSlots ? (
          <TimePicker
            slots={appointmentSlotsData.appointmentSlots}
            value={selectedDateTime}
            onChange={(time) => {
              setSelectedDateTime(time);
              handleSelectTime(time);
            }}
            onPrev={handlePrevDates}
            onNext={handleNextDates}
            showPrev={dateDifferenceFromNow(startDate)?.days >= 1}
          />
        ) : (
          <Text>No available appointment times found.</Text>
        )}
      </Box>
    </Flex>
  );
};

export default observer(Step7_TimeSelection);
